import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { PlayCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/layout/page-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DataTable,
  type Column,
} from "@/components/data/data-table";
import { EmptyState } from "@/components/data/empty-state";
import { ExecutionStatusBadge, ExecutionTypeBadge } from "@/components/data/status-badge";
import { RefreshButton } from "@/components/data/refresh-button";
import {
  DateRangeFilter,
  dayToIso,
} from "@/components/data/date-range-filter";
import {
  executionsApi,
  type Execution,
  type ExecutionStatus,
  type ExecutionType,
} from "@/api/executions";
import { queryKeys } from "@/api/query-keys";
import { useCursorList } from "@/hooks/use-cursor-list";
import { formatDateTime, formatRelative } from "@/lib/format";

const STATUS_VALUES: (ExecutionStatus | "all")[] = [
  "all",
  "scheduled",
  "running",
  "completed",
  "failed",
  "cancelled",
];

const searchSchema = z.object({
  status: z.string().optional(),
  type: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
});

export const Route = createFileRoute("/_authenticated/executions/")({
  validateSearch: searchSchema,
  component: ExecutionsPage,
});

function ExecutionsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const navigateTo = useNavigate();
  const { t } = useTranslation();
  const status = (search.status ?? "all") as ExecutionStatus | "all";
  const statusFilter = status === "all" ? undefined : status;
  const typeFilterRaw = (search.type ?? "all") as ExecutionType | "all";
  const typeFilter = typeFilterRaw === "all" ? undefined : typeFilterRaw;

  const listParams = {
    status: statusFilter,
    type: typeFilter,
    from: dayToIso(search.from, "start"),
    to: dayToIso(search.to, "end"),
    limit: 25,
  };
  const listQuery = useCursorList({
    queryKey: queryKeys.executions.list(listParams),
    fetcher: executionsApi.list,
    params: listParams,
  });

  const columns: Column<Execution>[] = [
    {
      id: "scheduled",
      header: t("executions.columns.scheduled"),
      cell: (e) => (
        <div>
          <p className="text-sm">{formatDateTime(e.scheduledAt)}</p>
          <p className="text-xs text-muted-foreground">
            {t(`executions.trigger.${e.triggeredBy}`)}
            {e.triggeredByUser ? ` · ${e.triggeredByUser}` : ""}
          </p>
        </div>
      ),
    },
    {
      id: "type",
      header: t("executions.columns.type"),
      cell: (e) => <ExecutionTypeBadge type={e.type} />,
    },
    {
      id: "status",
      header: t("executions.columns.status"),
      cell: (e) => <ExecutionStatusBadge status={e.status} />,
    },
    {
      id: "total",
      header: t("executions.columns.total"),
      cell: (e) => (
        <span className="text-sm tabular-nums font-medium">
          {e.totalRegistrations}
        </span>
      ),
      headClassName: "text-right",
      className: "text-right",
    },
    {
      id: "success",
      header: t("executions.columns.success"),
      cell: (e) => (
        <span className="text-sm tabular-nums text-success">
          {e.successCount}
        </span>
      ),
      headClassName: "text-right",
      className: "text-right",
    },
    {
      id: "failed",
      header: t("executions.columns.failed"),
      cell: (e) => (
        <span className="text-sm tabular-nums text-destructive">
          {e.failedCount}
        </span>
      ),
      headClassName: "text-right",
      className: "text-right",
    },
    {
      id: "skipped",
      header: t("executions.columns.skipped"),
      cell: (e) => (
        <span className="text-sm tabular-nums text-muted-foreground">
          {e.skippedCount}
        </span>
      ),
      headClassName: "text-right",
      className: "text-right",
    },
    {
      id: "ended",
      header: t("executions.columns.ended"),
      cell: (e) => (
        <span className="text-sm text-muted-foreground">
          {e.endedAt ? formatRelative(e.endedAt) : "—"}
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t("executions.title")}
        subtitle={t("executions.subtitle")}
        actions={
          <RefreshButton
            onClick={() => listQuery.refetch()}
            spinning={listQuery.isFetching}
          />
        }
      />

      <div className="flex gap-3 mb-4">
        <Select
          value={status}
          onValueChange={(v) =>
            navigate({
              search: (prev) => ({
                ...prev,
                status: v === "all" ? undefined : v,
              }),
              replace: true,
            })
          }
        >
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_VALUES.map((value) => (
              <SelectItem key={value} value={value}>
                {t(`executions.filters.status.${value}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={typeFilterRaw}
          onValueChange={(v) =>
            navigate({
              search: (prev) => ({ ...prev, type: v === "all" ? undefined : v }),
              replace: true,
            })
          }
        >
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("executions.filters.type.all")}</SelectItem>
            <SelectItem value="inscripcion">{t("executions.type.inscripcion")}</SelectItem>
            <SelectItem value="boleta">{t("executions.type.boleta")}</SelectItem>
          </SelectContent>
        </Select>
        <DateRangeFilter
          from={search.from}
          to={search.to}
          onChange={(range) =>
            navigate({
              search: (prev) => ({ ...prev, from: range.from, to: range.to }),
              replace: true,
            })
          }
        />
      </div>

      <DataTable<Execution>
        data={listQuery.items}
        columns={columns}
        getRowId={(e) => e.id}
        isLoading={listQuery.isLoading}
        isFetching={listQuery.isFetching}
        error={listQuery.error}
        onRowClick={(e) =>
          navigateTo({
            to: "/executions/$executionId",
            params: { executionId: e.id },
          })
        }
        loadMore={
          listQuery.hasNextPage
            ? {
                hasMore: true,
                isLoading: listQuery.isFetchingNextPage,
                onLoadMore: () => listQuery.fetchNextPage(),
              }
            : undefined
        }
        emptyState={
          <EmptyState
            icon={PlayCircle}
            title={
              status !== "all"
                ? t("executions.empty.noStatusTitle")
                : t("executions.empty.noneTitle")
            }
            description={
              status !== "all"
                ? t("executions.empty.noStatusDescription")
                : t("executions.empty.noneDescription")
            }
          />
        }
      />
    </>
  );
}
